import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { addToast } from '@heroui/react'
import { usePostHog } from '@posthog/react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { usePages } from '@/hooks'
import { useSubmitWithdrawal } from '@/hooks/use-submit-withdrawal'
import { NotFound } from '@/pages'
import { LandingLayout } from '@/pages/portal/login.page'
import { PortalHeader } from '@/ui/layout/portal-header'
import { PortalFooter } from '@/ui/layout/portal-footer'
import { getOrderParams } from '@/utils/get-order-params'
import {
  WithdrawalForm,
  type WithdrawalFormData
} from '@/app/withdrawal/withdrawal-form'
import { WithdrawalReview } from '@/app/withdrawal/withdrawal-review'
import {
  WithdrawalReceived,
  type ReceivedWithdrawal
} from '@/app/withdrawal/withdrawal-received'

// Same chrome as the return flow: misako gets neither header nor footer there either.
const PortalShell = ({ children }: { children: React.ReactNode }) => {
  const isMisako = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group === 'misako'
  )
  return (
    <div className="relative flex min-h-screen flex-col bg-white lg:bg-[#f2f2f2]">
      <PortalHeader />
      <div className="flex h-full flex-col items-center justify-center">
        {children}
      </div>
      {isMisako ? null : (
        <div className="mt-auto flex items-center p-4 lg:bg-[#F2F2F2] print:hidden">
          <PortalFooter />
        </div>
      )}
    </div>
  )
}

export const WithdrawalPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const posthog = usePostHog()
  const { pages } = usePages()
  const [searchParams, setSearchParams] = useSearchParams()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const ecommerceId = settings.ecommerce_id

  const [request, setRequest] = useState<WithdrawalFormData>()
  const [received, setReceived] = useState<ReceivedWithdrawal>()
  const { mutate, isPending } = useSubmitWithdrawal()

  const step = searchParams.get('step')

  useEffect(() => {
    if (!settings.withdrawal_enabled) return
    posthog.capture('Withdrawal Page Viewed', { ecommerceId })
  }, [posthog, ecommerceId, settings.withdrawal_enabled])

  if (!settings.withdrawal_enabled || !ecommerceId) return <NotFound />

  const confirm = (values: WithdrawalFormData) =>
    mutate(
      {
        ecommerceId,
        returnsSubmitWithdrawalRequest: {
          name: values.name,
          order_number: values.orderNumber,
          email: values.email,
          lang: i18n.language
        }
      },
      {
        onError: () => {
          addToast({
            title: t('withdrawal.error_toast_title'),
            description: t('withdrawal.error_toast_description'),
            color: 'danger'
          })
        },
        onSuccess: (data) => {
          posthog.capture('Withdrawal Submitted', {
            ecommerceId,
            orderShipped: !!data.order_shipped
          })
          setReceived({
            id: data.id ?? '',
            receivedAt: data.received_at ?? new Date().toISOString(),
            orderShipped: !!data.order_shipped,
            orderNumber: values.orderNumber,
            email: values.email
          })
          // Replace, so going back can't send the same withdrawal twice.
          setSearchParams({ step: 'received' }, { replace: true })
        }
      }
    )

  if (step === 'received' && received)
    return (
      <PortalShell>
        <WithdrawalReceived
          withdrawal={received}
          onStartReturn={() => {
            const params = getOrderParams(
              { orderNumber: received.orderNumber, email: received.email },
              settings.identification_flow!
            )
            navigate(`${pages.Landing}?${params.toString()}`)
          }}
        />
      </PortalShell>
    )

  // Once sent, a review left in the history shows the form instead.
  if (step === 'review' && request && !received)
    return (
      <PortalShell>
        <WithdrawalReview
          request={request}
          isSubmitting={isPending}
          onEdit={() => setSearchParams({}, { replace: true })}
          onConfirm={() => confirm(request)}
        />
      </PortalShell>
    )

  return (
    <LandingLayout>
      <header className="mb-2 space-y-2 text-center">
        <h1 className="text-xl [font-weight:750] [text-wrap:balance] md:text-2xl">
          {t('withdrawal.title')}
        </h1>
        <h2 className="mx-auto max-w-96 text-sm [font-weight:normal] [text-wrap:pretty]">
          {t('withdrawal.description')}
        </h2>
      </header>
      <WithdrawalForm
        defaultValues={request}
        onSubmit={(values) => {
          setRequest(values)
          setReceived(undefined)
          // A step URL showing the form (reloaded, or sent already) is replaced rather than stacked.
          setSearchParams({ step: 'review' }, { replace: step !== null })
        }}
      />
    </LandingLayout>
  )
}
